import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import { ShoppingCart } from 'lucide-react';
import { FaBoxes, FaCashRegister, FaChartLine, FaSync, FaMapMarkerAlt, FaGift, FaTag, FaTruck, FaChartBar, FaGlobe, FaFileInvoiceDollar } from 'react-icons/fa';
import '../style/products/mart.css';
import Header from '@/common/Header';
import Footer from '@/common/Footer';

import logo from '../img/quanto_light.webp';

const features = [
  { icon: FaBoxes, title: 'Centralized Inventory Management', description: 'Track and manage inventory across multiple categories, departments, and locations.' },
  { icon: FaCashRegister, title: 'Fast and Efficient Billing', description: 'Speed up the checkout process with easy-to-use billing systems.' },
  { icon: FaChartLine, title: 'Real-Time Sales Tracking', description: 'Monitor sales performance in real time across different categories and locations.' },
  { icon: FaSync, title: 'Automated Reordering', description: 'Set automated reordering triggers based on stock levels.' },
  { icon: FaMapMarkerAlt, title: 'Multi-Location Support', description: 'Manage multiple stores or departments under one system.' },
  { icon: FaGift, title: 'Loyalty Program Integration', description: 'Implement customized loyalty programs to reward frequent customers.' },
  { icon: FaTag, title: 'Promotions and Discount Management', description: 'Easily set up and manage promotions, seasonal discounts, and offers.' },
  { icon: FaTruck, title: 'Vendor and Supplier Management', description: 'Keep track of vendor relationships and manage purchase orders.' },
  { icon: FaChartBar, title: 'Advanced Analytics and Reporting', description: 'Access detailed reports on sales, customer behavior, inventory, and financials.' },
  { icon: FaGlobe, title: 'Seamless Integration with E-Commerce', description: 'Integrate with Quantofyy and other e-commerce platforms for unified operations.' },
  { icon: FaFileInvoiceDollar, title: 'GST Compliant Billing', description: 'Ensure all transactions are compliant with GST regulations.' },
];

const FeatureItem = ({ feature, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "center center"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, 0]);
  const opacity = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <motion.div
      ref={ref}
      style={{ y, opacity }}
      className={`feature-item-mart ${index % 2 === 0 ? 'flex-row-reverse-mart' : ''}`}
    >
      <div className={`feature-text-mart ${index % 2 === 0 ? 'text-right-mart' : 'text-left-mart'}`}>
        <h2 className="feature-title-mart">{feature.title}</h2>
        <p className="feature-description-mart">{feature.description}</p>
      </div>
      <div className="feature-icon-wrapper-mart">
        <feature.icon className="feature-icon-mart" />
      </div>
      <div className="flex-grow" />
    </motion.div>
  );
}

export default function QuantoMart() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const springConfig = { damping: 25, stiffness: 700 };
  const mouseX = useSpring(useMotionValue(0), springConfig);
  const mouseY = useSpring(useMotionValue(0), springConfig);
  const [isWhyChooseVisible, setIsWhyChooseVisible] = useState(true);
  const footerRef = useRef(null);
  const whyChooseRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    mouseX.set(mousePosition.x);
    mouseY.set(mousePosition.y);
  }, [mousePosition, mouseX, mouseY]);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsWhyChooseVisible(false);
        } else {
          setIsWhyChooseVisible(true);
        }
      });
    }, options);

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    return () => {
      if (footerRef.current) {
        observer.unobserve(footerRef.current);
      }
    };
  }, []);

  return (
    <>
      <Header />
      <section className="about-title-section">
        <h2><img alt="quantologo" src={logo} style={{ width: 280, marginLeft: 10, verticalAlign: "middle", marginBottom: 20 }} />Mart</h2>
      </section>
      <div className="container-mart">
        <motion.div
          initial={{ scale: 5, y: -100 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ duration: 1, type: 'spring' }}
          className="header-mart"
        >
          <ShoppingCart className="shopping-cart-icon-mart" />
          <h1 className="main-title-mart">Quanto Mart ERP Features</h1>
        </motion.div>

        <div className="features-container-mart">
          {features.map((feature, index) => (
            <FeatureItem key={index} feature={feature} index={index} />
          ))}
        </div>

        <motion.div
          className="cursor-mart"
          style={{
            x: mouseX,
            y: mouseY,
          }}
        >
          <ShoppingCart className="cursor-icon-mart" />
        </motion.div>

        {isWhyChooseVisible && (
          <div className="why-choose-mart" ref={whyChooseRef}>
            <p className="why-choose-title-mart">Why Choose Quanto Mart ERP?</p>
            <p className="why-choose-description-mart">
              Quanto Mart is the perfect solution for departmental stores and hypermarkets aiming to streamline operations,
              manage large inventories, and enhance customer satisfaction.
            </p>
          </div>
        )}
      </div>
      <Footer ref={footerRef} />
    </>
  );
}